import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Market } from "../../markets/marketData";

export const useMarketToggle = (
  markets: Market[], 
  setMarkets: React.Dispatch<React.SetStateAction<Market[]>>, 
  organizationId: string | null, 
  dbError: boolean,
  missingColumns: string[] = [],
  fetchCurrentMarkets: () => Promise<void>
) => {
  const { toast } = useToast();

  // Function to toggle market active state
  const toggleMarketActive = async (market: Market, isActive: boolean) => {
    if (dbError || !organizationId) {
      // Even with DB error, we'll update local state
      const updatedMarkets = markets.map(m => 
        m.country === market.country ? { ...m, isActive } : m
      );
      setMarkets(updatedMarkets);
      
      toast({
        title: "Mode Local",
        description: `Le marché ${market.country} a été ${isActive ? 'activé' : 'désactivé'} en local uniquement.`,
        variant: "default",
      });
      return;
    }

    try {
      // Update local state first for UI responsiveness
      const updatedMarkets = markets.map(m => 
        m.country === market.country ? { ...m, isActive } : m
      );
      setMarkets(updatedMarkets);
      
      // Prepare data for database update
      const activeMarkets = updatedMarkets.filter(m => m.isActive).map(m => m.country);
      
      console.log("Toggling market state:", market.country, isActive);
      console.log("Updated active markets:", activeMarkets);
      
      // Try to use the RPC function first for more reliable updates
      if (!missingColumns.includes('active_markets')) {
        try {
          const { error: rpcError } = await supabase.rpc(
            'update_organization_markets',
            { 
              p_organization_id: organizationId,
              p_default_market: markets.find(m => m.isDefault)?.country || markets[0].country,
              p_enabled_markets: markets.map(m => m.country),
              p_active_markets: activeMarkets
            }
          );
          
          if (!rpcError) {
            toast({
              title: "Marché mis à jour",
              description: `Le marché ${market.country} est maintenant ${isActive ? 'activé' : 'désactivé'}.`,
            });
            return;
          }
          
          // If RPC fails, fall back to direct update
          console.warn("RPC update failed, falling back to direct update:", rpcError);
        } catch (rpcError) {
          console.error("Error with RPC update:", rpcError);
        }
      }
      
      // Direct update
      const updateData: any = { 
        updated_at: new Date().toISOString()
      };
      
      // Only add active_markets if it exists
      if (!missingColumns.includes('active_markets')) {
        updateData.active_markets = activeMarkets;
      }
      
      const { error } = await supabase
        .from('organizations')
        .update(updateData)
        .eq('id', organizationId);
      
      if (error) {
        // If direct update fails due to missing column, use local-only mode
        if (error.code === "42703") {
          console.warn(`Column doesn't exist: ${error.message}`);
          
          toast({
            title: "Mise à jour locale",
            description: `Le marché ${market.country} a été ${isActive ? 'activé' : 'désactivé'} localement seulement.`,
          });
          return;
        }
        
        throw error;
      }
      
      toast({
        title: "Marché mis à jour",
        description: `Le marché ${market.country} est maintenant ${isActive ? 'activé' : 'désactivé'}.`,
      });
    } catch (error) {
      console.error("Erreur lors de la mise à jour du marché:", error);
      
      // Keep local state changes but inform user
      toast({
        title: "Mode Local",
        description: `Le marché ${market.country} a été ${isActive ? 'activé' : 'désactivé'} en local uniquement.`,
        variant: "default",
      });
    }
  };

  return { toggleMarketActive };
};
